import { create } from 'zustand';
import config, { ROUTES } from '../config';
import { debounce } from '@instructure/debounce';
import {
  updateNavigation,
  replacePathPlaceholders,
} from '../helpers/utilities';
import memoize from 'memoize';
import useSessionStore from './session';
import useAppStore from './app';

const useLayoutStore = create((set, get) => ({
  mode: 'overview',

  roomContainer: {
    elementRef: null,
    dimensions: { width: null, height: null },
  },
  roomContent: {
    elementRef: null,
  },
  drawer: {
    elementRef: null,
  },
  room: {
    elementRef: null,
  },
  sessionCard: {},
  sessionDimensions: {},

  initialize: (data) => {
    const localRoomContainer = { ...get().roomContainer };
    const localRoomContent = { ...get().roomContent };
    const localDrawer = { ...get().drawer };

    localRoomContainer.elementRef = data.regions.roomContainer;
    localRoomContent.elementRef = data.regions.roomContent;
    localDrawer.elementRef = data.regions.drawer;

    set({
      roomContainer: localRoomContainer,
      roomContent: localRoomContent,
      drawer: localDrawer,
    });

    const observer = new ResizeObserver(() => {
      console.log('Resize');
      get().calculateLayoutSizing();
    });

    observer.observe(get().roomContent.elementRef.current);
  },

  /**
   * Calculates the optimal size of a session element within a given container
   * based on the number of sessions and aspect ratio.
   *
   * @param {number} containerWidth
   * @param {number} containerHeight
   * @param {number} sessionCount
   * @param {array} aspectRatio
   * @return {object}
   *   {width: number, height: number, columns: number, rows: number}
   */
  calculateSessionSize: memoize(
    (containerWidth, containerHeight, sessionCount, aspectRatio = [16, 9]) => {
      if (sessionCount <= 0) {
        throw new Error('Session count must be greater than 0');
      }

      const aspectW = aspectRatio[0];
      const aspectH = aspectRatio[1];

      let bestCols = 1;
      let bestRows = sessionCount;
      let bestWidth = 0;
      let bestHeight = 0;
      let bestFit = 0;

      for (let cols = 1; cols <= sessionCount; cols++) {
        let rows = Math.ceil(sessionCount / cols);

        // Calculate width-first session size while maintaining aspect ratio
        let sessionWidth = Math.floor(containerWidth / cols);
        let sessionHeight = Math.floor((sessionWidth * aspectH) / aspectW); // Maintain aspect ratio

        // If it exceeds the container height, adjust using height-first approach
        if (sessionHeight * rows > containerHeight) {
          sessionHeight = Math.floor(containerHeight / rows);
          sessionWidth = Math.floor((sessionHeight * aspectW) / aspectH); // Adjust width to maintain aspect ratio
        }

        let totalUsedWidth = sessionWidth * cols;
        let totalUsedHeight = sessionHeight * rows;

        // Ensure it fully fits inside the container and maximizes area usage
        if (
          totalUsedWidth <= containerWidth &&
          totalUsedHeight <= containerHeight
        ) {
          let fitArea = totalUsedWidth * totalUsedHeight;

          if (fitArea > bestFit) {
            bestFit = fitArea;
            bestCols = cols;
            bestRows = rows;
            bestWidth = sessionWidth;
            bestHeight = sessionHeight;
          }
        }
      }

      return {
        width: Math.floor(bestWidth * 0.99),
        height: Math.floor(bestHeight * 0.99),
        columns: bestCols,
        rows: bestRows,
      };
    }
  ),

  /**
   * Calculates the optimal dimensions for the testing area, taking into account the drawer width
   * and the number of sessions. It will then set the session dimensions in the state.
   */
  calculateLayoutSizing: () => {
    const localRoomContainer = { ...get().roomContainer };

    const roomDimensions =
      get().roomContent.elementRef.current.getBoundingClientRect();

    const sessionCount =
      Object.keys(useSessionStore.getState().testing)?.length || 0;

    if (sessionCount > 0) {
      const sessionSize = get().calculateSessionSize(
        roomDimensions.width,
        roomDimensions.height,
        sessionCount
      );

      localRoomContainer.dimensions = {
        width: roomDimensions.width,
        height: roomDimensions.height,
      };
      set({
        sessionDimensions: {
          width: sessionSize.width,
          height: sessionSize.height,
        },
        roomContainer: localRoomContainer,
      });
    }
  },
}));

export default useLayoutStore;
